import { useQuery } from '@tanstack/react-query'
import { Archive, ArchiveRestore, ArrowDown, ArrowUp, CalendarPlus, Plus, Save, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { toApiError } from '@/api/errors'
import { ErrorState, LoadingState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { FormField } from '@/components/forms/FormField'
import { Button, IconButton } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Input } from '@/components/ui/Input'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { VideoEmbed } from '@/components/ui/VideoEmbed'
import { subjectsQuery } from '@/features/academic/api'
import {
  courseQuery,
  runsQuery,
  useArchiveCourse,
  useDeleteCourse,
  useSaveCourse,
} from '@/features/courses/api'
import { RunDialog } from '@/features/courses/components/RunDialog'
import { RunList } from '@/features/courses/components/RunList'
import type { Course, Lesson } from '@/features/courses/types'
import type { MaterialKind } from '@/features/lms/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

const KINDS: { value: MaterialKind; label: string }[] = [
  { value: 'lesson', label: 'Lesson' },
  { value: 'video', label: 'Video' },
  { value: 'resource', label: 'Resource' },
  { value: 'link', label: 'Link' },
]

type Draft = Omit<Lesson, 'id'> & { key: string }
const newKey = () => Math.random().toString(36).slice(2)

/** Creates or edits a catalogue course: details, and its content (units and lessons, in order). */
export function CourseEditPage() {
  const { id } = useParams()
  const courseId = Number(id) || null
  const course = useQuery({ ...courseQuery(courseId ?? 0), enabled: courseId !== null })
  if (courseId !== null && course.isPending) return <LoadingState />
  if (courseId !== null && course.error) return <ErrorState onRetry={() => void course.refetch()} />
  return <CourseForm key={courseId ?? 'new'} course={course.data ?? null} />
}

function CourseForm({ course }: { course: Course | null }) {
  const { t } = useTranslation('courses')
  useDocumentTitle(course?.name ?? 'New course')
  const navigate = useNavigate()
  const subjects = useQuery(subjectsQuery())
  const runs = useQuery({ ...runsQuery({ courseId: course?.id }), enabled: course !== null })
  const save = useSaveCourse()
  const archive = useArchiveCourse()
  const remove = useDeleteCourse()
  const [name, setName] = useState(course?.name ?? '')
  const [code, setCode] = useState(course?.code ?? '')
  const [weeks, setWeeks] = useState(course?.durationWeeks?.toString() ?? '')
  const [subjectId, setSubjectId] = useState(course ? String(course.subject.id) : '')
  const [description, setDescription] = useState(course?.description ?? '')
  const [lessons, setLessons] = useState<Draft[]>(() =>
    (course?.lessons ?? []).map((l) => ({ ...l, key: newKey() })),
  )
  const [scheduling, setScheduling] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const patch = (key: string, changes: Partial<Draft>) =>
    setLessons((all) => all.map((l) => (l.key === key ? { ...l, ...changes } : l)))
  const move = (index: number, by: number) =>
    setLessons((all) => {
      const next = [...all]
      const [item] = next.splice(index, 1)
      if (item) next.splice(index + by, 0, item)
      return next
    })

  const submit = async () => {
    try {
      const saved = await save.mutateAsync({
        id: course?.id,
        name,
        code: code || null,
        description: description || null,
        durationWeeks: weeks ? Number(weeks) : null,
        subjectId: subjectId ? Number(subjectId) : null,
        lessons: lessons.map((l) => ({
          kind: l.kind,
          title: l.title,
          unit: l.unit || null,
          body: l.body || null,
          url: l.url || null,
        })),
      })
      toast.success(course ? t('Course saved') : t('Course created'), saved.name)
      if (!course) void navigate(`/admin/courses/${saved.id}`, { replace: true })
    } catch (e) {
      toast.error(t('The course was not saved'), toApiError(e).message)
    }
  }

  return (
    <>
      <PageHeader
        title={course?.name ?? t('New course')}
        breadcrumbs={[
          { label: t('Courses') },
          { label: t('Catalogue'), to: '/admin/courses' },
          { label: course?.name ?? t('New course') },
        ]}
        homeHref="/admin"
        actions={
          <>
            {course ? (
              <>
                <Button
                  variant="secondary"
                  leftIcon={<CalendarPlus />}
                  onClick={() => setScheduling(true)}
                  disabled={course.archived}
                >
                  {t('Schedule for a group')}
                </Button>
                <IconButton
                  label={course.archived ? t('Restore') : t('Archive')}
                  icon={course.archived ? <ArchiveRestore /> : <Archive />}
                  variant="secondary"
                  onClick={() => archive.mutate({ id: course.id, archived: !course.archived })}
                />
                {course.runCount === 0 ? (
                  <IconButton
                    label={t('Delete')}
                    icon={<Trash2 />}
                    variant="danger-soft"
                    onClick={() => setDeleting(true)}
                  />
                ) : null}
              </>
            ) : null}
            <Button
              leftIcon={<Save />}
              onClick={() => void submit()}
              loading={save.isPending}
              disabled={name.trim() === ''}
            >
              {t('Save')}
            </Button>
          </>
        }
      />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-6">
          <Card>
            <CardHeader title={t('Course')} />
            <CardBody className="grid gap-4 sm:grid-cols-2">
              <FormField label={t('Name')} required className="sm:col-span-2">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                  placeholder={t('e.g. General English A1')}
                />
              </FormField>
              <FormField label={t('Code')}>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  maxLength={30}
                  placeholder="GE-A1"
                />
              </FormField>
              <FormField label={t('Duration (weeks)')}>
                <Input
                  type="number"
                  min={1}
                  max={104}
                  value={weeks}
                  onChange={(e) => setWeeks(e.target.value)}
                />
              </FormField>
              <FormField
                label={t('Subject taught')}
                hint={t('Left empty, a subject named after the course is created.')}
                className="sm:col-span-2"
              >
                <Select
                  placeholder={t('After the course name')}
                  value={subjectId}
                  onChange={(e) => setSubjectId(e.target.value)}
                  options={(subjects.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
                />
              </FormField>
              <FormField label={t('Description')} className="sm:col-span-2">
                <Textarea
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={5000}
                />
              </FormField>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title={t('Content')}
              description={t('Copied into the online learning of each group the course is scheduled for.')}
              actions={
                <Button
                  size="sm"
                  variant="secondary"
                  leftIcon={<Plus />}
                  onClick={() =>
                    setLessons((all) => [
                      ...all,
                      {
                        key: newKey(),
                        kind: 'lesson',
                        title: '',
                        unit: all.at(-1)?.unit ?? '',
                        body: '',
                        url: '',
                      },
                    ])
                  }
                >
                  {t('Add a lesson')}
                </Button>
              }
            />
            <CardBody className="space-y-4">
              {lessons.length === 0 ? <p className="text-sm text-muted">{t('No lessons yet.')}</p> : null}
              {lessons.map((l, i) => (
                <fieldset key={l.key} className="rounded-card border border-line p-4">
                  <legend className="sr-only">{t('Lesson {{number}}', { number: i + 1 })}</legend>
                  <div className="mb-3 flex items-center gap-2">
                    <span className="grid size-7 place-items-center rounded-full bg-brand-50 text-xs font-semibold text-brand-700">
                      {i + 1}
                    </span>
                    <span className="flex-1" />
                    <IconButton
                      label={t('Move up')}
                      icon={<ArrowUp />}
                      size="icon-sm"
                      variant="ghost"
                      disabled={i === 0}
                      onClick={() => move(i, -1)}
                    />
                    <IconButton
                      label={t('Move down')}
                      icon={<ArrowDown />}
                      size="icon-sm"
                      variant="ghost"
                      disabled={i === lessons.length - 1}
                      onClick={() => move(i, 1)}
                    />
                    <IconButton
                      label={t('Remove')}
                      icon={<Trash2 />}
                      size="icon-sm"
                      variant="ghost"
                      onClick={() => setLessons((all) => all.filter((x) => x.key !== l.key))}
                    />
                  </div>
                  <div className="grid gap-3 sm:grid-cols-[10rem_minmax(0,1fr)_12rem]">
                    <FormField label={t('Type')}>
                      <Select
                        value={l.kind}
                        onChange={(e) => patch(l.key, { kind: e.target.value as MaterialKind })}
                        options={KINDS.map((k) => ({ value: k.value, label: t(k.label) }))}
                      />
                    </FormField>
                    <FormField label={t('Title')} required>
                      <Input
                        value={l.title}
                        onChange={(e) => patch(l.key, { title: e.target.value })}
                        maxLength={200}
                      />
                    </FormField>
                    <FormField label={t('Unit')}>
                      <Input
                        value={l.unit ?? ''}
                        onChange={(e) => patch(l.key, { unit: e.target.value })}
                        maxLength={80}
                        placeholder={t('e.g. Unit 1')}
                      />
                    </FormField>
                  </div>
                  {l.kind !== 'lesson' ? (
                    <FormField
                      label={l.kind === 'video' ? t('Video link (YouTube, Vimeo)') : t('Link')}
                      className="mt-3"
                    >
                      <Input
                        type="url"
                        value={l.url ?? ''}
                        onChange={(e) => patch(l.key, { url: e.target.value })}
                        placeholder="https://"
                      />
                    </FormField>
                  ) : null}
                  <FormField label={t('Text')} className="mt-3">
                    <Textarea
                      rows={l.kind === 'lesson' ? 6 : 3}
                      value={l.body ?? ''}
                      onChange={(e) => patch(l.key, { body: e.target.value })}
                    />
                  </FormField>
                  {l.kind === 'video' && l.url ? (
                    <div className="mt-3 max-w-md">
                      <VideoEmbed url={l.url} title={l.title} />
                    </div>
                  ) : null}
                </fieldset>
              ))}
            </CardBody>
          </Card>
        </div>

        {course ? (
          <Card className="self-start">
            <CardHeader title={t('Scheduled for')} />
            <CardBody>
              <RunList runs={runs.data ?? []} compact emptyText={t('Not scheduled yet.')} />
            </CardBody>
          </Card>
        ) : null}
      </div>

      {course ? <RunDialog open={scheduling} onOpenChange={setScheduling} courseId={course.id} /> : null}
      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title={t('Delete this course?')}
        description={t('Its content is deleted too. This cannot be undone.')}
        confirmLabel={t('Delete')}
        tone="danger"
        loading={remove.isPending}
        onConfirm={() =>
          course &&
          remove.mutate(course.id, {
            onSuccess: () => void navigate('/admin/courses'),
            onError: (e) => toast.error(t('Could not delete the course'), toApiError(e).message),
          })
        }
      />
    </>
  )
}
